<template>
  <div class="shop-coupon-page">
    <section class="page-toolbar">
      <div class="toolbar-copy">
        <strong>平台优惠券</strong>
        <span>平台券对所有买家可见，可在商城「我的 → 优惠券 → 可领取」里领取；店铺券由商户在自己的后台发放，这里只能查看。</span>
      </div>
      <div class="toolbar-actions">
        <button class="plain-button" type="button" :disabled="loading" @click="load">
          {{ loading ? "加载中..." : "刷新" }}
        </button>
        <button class="primary-button" type="button" @click="openEditor(null)">新建平台券</button>
      </div>
    </section>

    <p v-if="error" class="service-error">{{ error }}</p>

    <section v-if="editor" class="coupon-editor">
      <header class="coupon-editor__head">
        <strong>{{ editor.id ? "编辑平台券" : "新建平台券" }}</strong>
        <div>
          <button class="plain-button" type="button" @click="editor = null">取消</button>
          <button class="primary-button" type="button" :disabled="saving" @click="submit">
            {{ saving ? "保存中..." : "保存" }}
          </button>
        </div>
      </header>
      <div class="coupon-editor__grid">
        <label>
          <span>优惠券名称</span>
          <input v-model="editor.title" type="text" placeholder="如 全场满 99 减 10" />
        </label>
        <label>
          <span>优惠金额（元）</span>
          <input v-model="editor.discountAmount" type="number" min="0" step="0.01" placeholder="10" />
        </label>
        <label>
          <span>使用门槛（元）</span>
          <input v-model="editor.minAmount" type="number" min="0" step="0.01" placeholder="0 表示无门槛" />
        </label>
        <label>
          <span>发放数量</span>
          <input v-model="editor.totalQty" type="number" min="0" step="1" placeholder="0 表示不限量" />
        </label>
        <label>
          <span>有效期至</span>
          <input v-model="editor.validTo" type="date" />
        </label>
        <label>
          <span>状态</span>
          <select v-model="editor.enabled">
            <option :value="true">启用</option>
            <option :value="false">停用</option>
          </select>
        </label>
        <label class="coupon-editor__full">
          <span>使用说明</span>
          <input v-model="editor.description" type="text" placeholder="如 全场商品可用，不与其他优惠叠加" />
        </label>
      </div>
    </section>

    <section class="coupon-stats">
      <div><span>优惠券总数</span><strong>{{ summary.total || 0 }}</strong></div>
      <div><span>平台券</span><strong>{{ summary.platformCount || 0 }}</strong></div>
      <div><span>店铺券</span><strong>{{ summary.storeCount || 0 }}</strong></div>
      <div><span>累计领取</span><strong>{{ summary.claimedTotal || 0 }}</strong></div>
      <div><span>累计使用</span><strong>{{ summary.usedTotal || 0 }}</strong></div>
    </section>

    <section class="coupon-table-wrap">
      <table class="coupon-table">
        <thead>
          <tr>
            <th>优惠券</th>
            <th>归属</th>
            <th>优惠</th>
            <th>门槛</th>
            <th>有效期至</th>
            <th>已领取 / 已使用</th>
            <th>状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="coupon in coupons" :key="coupon.id">
            <td data-label="优惠券">
              <b>{{ coupon.title }}</b>
              <small v-if="coupon.description">{{ coupon.description }}</small>
            </td>
            <td data-label="归属">
              <span class="coupon-tag" :class="coupon.platform ? 'platform' : 'store'">
                {{ coupon.platform ? "平台券" : (coupon.storeName || "店铺券") }}
              </span>
            </td>
            <td class="num strong" data-label="优惠金额">¥{{ money(coupon.discountAmount) }}</td>
            <td class="num" data-label="使用门槛">{{ Number(coupon.minAmount || 0) > 0 ? `满 ¥${money(coupon.minAmount)}` : "无门槛" }}</td>
            <td data-label="有效期至">{{ coupon.validTo ? String(coupon.validTo).slice(0, 10) : "长期有效" }}</td>
            <td class="num" data-label="领取 / 使用">{{ coupon.claimedQty || 0 }} / {{ coupon.usedQty || 0 }}</td>
            <td data-label="状态"><span class="coupon-state" :class="coupon.state">{{ stateLabel(coupon.state) }}</span></td>
            <td data-label="操作">
              <div class="coupon-actions">
                <template v-if="coupon.platform">
                  <button class="plain-button small" type="button" @click="openEditor(coupon)">编辑</button>
                  <button class="plain-button small" type="button" @click="toggleStatus(coupon)">
                    {{ coupon.state === "active" ? "停用" : "启用" }}
                  </button>
                  <button class="plain-button small danger" type="button" @click="remove(coupon)">删除</button>
                </template>
                <span v-else class="coupon-readonly">商户端维护</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-if="!loading && !coupons.length" class="empty-state">还没有优惠券，点右上角「新建平台券」发一张</p>
    </section>
  </div>
</template>

<script>
import request from "../utils/request"

export default {
  name: "ShopCouponView",
  data() {
    return {
      loading: true,
      saving: false,
      error: "",
      coupons: [],
      summary: {},
      editor: null,
    }
  },
  created() {
    this.load()
  },
  methods: {
    money(value) {
      return Number(value || 0).toFixed(2)
    },
    stateLabel(state) {
      return { active: "进行中", disabled: "已停用", expired: "已过期" }[state] || state
    },
    async load() {
      this.loading = true
      this.error = ""
      try {
        const payload = await request.get("/admin/shop/coupons")
        this.coupons = payload.data || []
        this.summary = payload.summary || {}
      } catch (error) {
        this.error = error.message
      } finally {
        this.loading = false
      }
    },
    openEditor(coupon) {
      this.editor = coupon
        ? {
            id: coupon.id,
            title: coupon.title,
            description: coupon.description || "",
            discountAmount: String(coupon.discountAmount ?? ""),
            minAmount: String(coupon.minAmount ?? 0),
            totalQty: String(coupon.totalQty ?? 0),
            validTo: coupon.validTo ? String(coupon.validTo).slice(0, 10) : "",
            enabled: coupon.state === "active",
          }
        : { id: null, title: "", description: "", discountAmount: "", minAmount: "0", totalQty: "0", validTo: "", enabled: true }
    },
    async submit() {
      const editor = this.editor
      if (!editor) return
      const title = String(editor.title || "").trim()
      const discount = Number(editor.discountAmount)
      const minAmount = Number(editor.minAmount || 0)
      const totalQty = Number(editor.totalQty || 0)
      if (!title) { this.error = "请填写优惠券名称"; return }
      if (!Number.isFinite(discount) || discount <= 0) { this.error = "优惠金额必须大于 0"; return }
      if (minAmount > 0 && minAmount < discount) { this.error = "使用门槛不能低于优惠金额"; return }
      this.saving = true
      this.error = ""
      try {
        const body = {
          title,
          description: String(editor.description || "").trim(),
          discountAmount: discount,
          minAmount,
          totalQty: Number.isFinite(totalQty) && totalQty > 0 ? totalQty : 0,
          validTo: editor.validTo || null,
          status: editor.enabled ? 1 : 0,
        }
        const payload = editor.id
          ? await request.patch(`/admin/shop/coupons/${editor.id}`, body)
          : await request.post("/admin/shop/coupons", body)
        this.coupons = payload.data?.data || payload.data || []
        this.summary = payload.data?.summary || this.summary
        this.editor = null
      } catch (error) {
        this.error = error.message
      } finally {
        this.saving = false
      }
    },
    async toggleStatus(coupon) {
      try {
        const body = {
          title: coupon.title,
          description: coupon.description || "",
          discountAmount: coupon.discountAmount,
          minAmount: coupon.minAmount,
          totalQty: coupon.totalQty,
          validTo: coupon.validTo ? String(coupon.validTo).slice(0, 10) : null,
          status: coupon.state === "active" ? 0 : 1,
        }
        const payload = await request.patch(`/admin/shop/coupons/${coupon.id}`, body)
        this.coupons = payload.data?.data || payload.data || []
        this.summary = payload.data?.summary || this.summary
      } catch (error) {
        this.error = error.message
      }
    },
    async remove(coupon) {
      if (!window.confirm(`确认删除「${coupon.title}」？已有人领取的券会改为停用。`)) return
      try {
        const payload = await request.delete(`/admin/shop/coupons/${coupon.id}`)
        this.coupons = payload.data?.data || payload.data || []
        this.summary = payload.data?.summary || this.summary
      } catch (error) {
        this.error = error.message
      }
    },
  },
}
</script>

<style scoped>
.shop-coupon-page { display: flex; flex-direction: column; gap: 16px; }
.coupon-stats { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px; }
.coupon-stats div { padding: 14px 16px; border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(20, 40, 30, .05); }
.coupon-stats span { display: block; margin-bottom: 6px; color: var(--ink-muted, #8a949c); font-size: 12px; }
.coupon-stats strong { font-size: 20px; }
.coupon-editor { padding: 18px 20px; border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(20, 40, 30, .06); }
.coupon-editor__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.coupon-editor__head strong { font-size: 16px; }
.coupon-editor__head div { display: flex; gap: 8px; }
.coupon-editor__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px 18px; }
.coupon-editor__full { grid-column: 1 / -1; }
.coupon-editor__grid label { display: flex; flex-direction: column; gap: 6px; }
.coupon-editor__grid span { color: #5e6773; font-size: 13px; }
.coupon-editor__grid input, .coupon-editor__grid select { height: 40px; padding: 0 12px; border: 1px solid #e4e8ea; border-radius: 9px; outline: 0; font-size: 14px; }
.coupon-table-wrap { overflow-x: auto; border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(20, 40, 30, .05); }
.coupon-table { width: 100%; min-width: 900px; border-collapse: collapse; }
.coupon-table th, .coupon-table td { padding: 12px 14px; border-bottom: 1px solid #f0f2f1; font-size: 13.5px; text-align: left; }
.coupon-table th { color: #6b747c; background: #fafbfb; font-weight: 500; }
.coupon-table td b { display: block; }
.coupon-table td small { color: #98a3aa; font-size: 12px; }
.coupon-table .num { text-align: right; }
.coupon-table .strong { font-weight: 700; }
.coupon-tag { padding: 3px 9px; border-radius: 5px; font-size: 12px; }
.coupon-tag.platform { color: #0d9e60; background: #eaf7f0; }
.coupon-tag.store { color: #b07d17; background: #fdf3dd; }
.coupon-state { padding: 3px 9px; border-radius: 5px; font-size: 12px; }
.coupon-state.active { color: #0d9e60; background: #eaf7f0; }
.coupon-state.disabled { color: #7b848a; background: #f2f4f5; }
.coupon-state.expired { color: #d98218; background: #fdf3e4; }
.coupon-actions { display: flex; gap: 8px; }
.coupon-readonly { color: #98a3aa; font-size: 12.5px; }
.plain-button.danger { color: #e2554a; }
.primary-button { padding: 8px 16px; border: 0; border-radius: 8px; color: #fff; background: #0d9e60; font-size: 13px; cursor: pointer; }
.primary-button:disabled { opacity: .6; }
.empty-state { padding: 30px 0; color: #98a3aa; font-size: 13px; text-align: center; }
</style>
